import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchEditorSubmissions, updatePaperStatus, assignReviewers } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const EditorDashboard = () => {
  const { user } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [availableReviewers, setAvailableReviewers] = useState([]);
  const [selectedReviewers, setSelectedReviewers] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState({ type: null, message: null });

  useEffect(() => {
    const loadSubmissions = async () => {
      try {
        setLoading(true);
        const data = await fetchEditorSubmissions();
        setSubmissions(data);
        setFilteredSubmissions(data);
      } catch (err) {
        console.error('Error loading submissions:', err);
        setError('Failed to load submissions. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadSubmissions();
  }, []);

  useEffect(() => {
    // Filter submissions based on selected status
    if (statusFilter === 'all') {
      setFilteredSubmissions(submissions);
    } else {
      setFilteredSubmissions(
        submissions.filter(paper => paper.status === statusFilter)
      );
    }
  }, [statusFilter, submissions]);

  const handleStatusChange = async (paperId, newStatus, comments = '') => {
    if (!window.confirm(`Are you sure you want to change this paper's status to ${newStatus.replace(/_/g, ' ')}?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await updatePaperStatus(paperId, newStatus, comments);
      
      // Update local state
      const updatedSubmissions = submissions.map(paper => 
        paper.id === paperId ? { ...paper, status: newStatus } : paper
      );
      
      setSubmissions(updatedSubmissions);
      
      setActionResult({
        type: 'success',
        message: `Paper status successfully updated to ${newStatus.replace(/_/g, ' ')}.`
      });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setActionResult({ type: null, message: null });
      }, 5000);
    } catch (err) {
      console.error('Error updating paper status:', err);
      setActionResult({
        type: 'error',
        message: 'Failed to update paper status. Please try again.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignReviewers = async (paperId) => {
    if (selectedReviewers.length === 0) {
      setActionResult({
        type: 'error',
        message: 'Please select at least one reviewer.'
      });
      return;
    }

    try {
      setActionLoading(true);
      await assignReviewers(paperId, selectedReviewers);
      
      // Update local state
      const updatedSubmissions = submissions.map(paper => 
        paper.id === paperId ? { ...paper, status: 'UNDER_REVIEW' } : paper
      );
      
      setSubmissions(updatedSubmissions);
      setSelectedPaper(null);
      setSelectedReviewers([]);
      
      setActionResult({
        type: 'success',
        message: 'Reviewers successfully assigned to paper.'
      });
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setActionResult({ type: null, message: null });
      }, 5000);
    } catch (err) {
      console.error('Error assigning reviewers:', err);
      setActionResult({
        type: 'error',
        message: 'Failed to assign reviewers. Please try again.'
      });
    } finally {
      setActionLoading(false);
    }
  };

  const openAssignReviewersModal = async (paper) => {
    setSelectedPaper(paper);
    
    // Here you would fetch available reviewers from the backend
    // For now we'll use a mock list
    setAvailableReviewers([
      { id: 1, name: 'Dr. Jane Smith', expertise: 'Agricultural Economics' },
      { id: 2, name: 'Prof. Michael Johnson', expertise: 'Crop Science' },
      { id: 3, name: 'Dr. Samuel Wilson', expertise: 'Soil Science' },
      { id: 4, name: 'Dr. Emily Davis', expertise: 'Animal Science' },
      { id: 5, name: 'Prof. Robert Brown', expertise: 'Environmental Sciences' }
    ]);
    
    setSelectedReviewers([]);
  };

  const handleReviewerSelection = (reviewerId) => {
    if (selectedReviewers.includes(reviewerId)) {
      setSelectedReviewers(selectedReviewers.filter(id => id !== reviewerId));
    } else {
      setSelectedReviewers([...selectedReviewers, reviewerId]);
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'SUBMITTED':
        return 'bg-info';
      case 'UNDER_REVIEW':
        return 'bg-primary';
      case 'ACCEPTED':
        return 'bg-success';
      case 'REJECTED':
        return 'bg-danger';
      case 'REVISIONS_REQUIRED':
        return 'bg-warning';
      case 'WITHDRAWN':
        return 'bg-secondary';
      case 'PUBLISHED':
        return 'bg-success';
      default:
        return 'bg-light text-dark';
    }
  };

  const renderStatusOptions = (paper) => {
    switch (paper.status) {
      case 'SUBMITTED':
        return (
          <>
            <button 
              className="dropdown-item"
              onClick={() => openAssignReviewersModal(paper)}
            >
              Assign Reviewers
            </button>
            <button 
              className="dropdown-item"
              onClick={() => handleStatusChange(paper.id, 'REJECTED')}
            >
              Reject Submission
            </button>
          </>
        );
      case 'UNDER_REVIEW':
        return (
          <>
            <button 
              className="dropdown-item"
              onClick={() => handleStatusChange(paper.id, 'ACCEPTED')}
            >
              Accept Paper
            </button>
            <button 
              className="dropdown-item"
              onClick={() => handleStatusChange(paper.id, 'REJECTED')}
            >
              Reject Paper
            </button>
            <button 
              className="dropdown-item"
              onClick={() => handleStatusChange(paper.id, 'REVISIONS_REQUIRED')}
            >
              Request Revisions
            </button>
          </>
        );
      case 'ACCEPTED':
        return (
          <button 
            className="dropdown-item"
            onClick={() => handleStatusChange(paper.id, 'PUBLISHED')}
          >
            Publish Paper
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container py-5">
      <div className="row mb-4">
        <div className="col-md-8">
          <h1>Editor Dashboard</h1>
          <p className="lead">
            Welcome, {user?.firstName} {user?.lastName}. Manage paper submissions and reviews here.
          </p>
        </div>
      </div>

      {actionResult.message && (
        <div className={`alert ${actionResult.type === 'success' ? 'alert-success' : 'alert-danger'}`} role="alert">
          {actionResult.message}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <div className="row align-items-center">
            <div className="col">
              <h3 className="mb-0">Submissions</h3>
            </div>
            <div className="col-auto">
              <select 
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Submissions</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="UNDER_REVIEW">Under Review</option>
                <option value="ACCEPTED">Accepted</option>
                <option value="REJECTED">Rejected</option>
                <option value="REVISIONS_REQUIRED">Revisions Required</option>
                <option value="PUBLISHED">Published</option>
              </select>
            </div>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : error ? (
            <div className="alert alert-danger">{error}</div>
          ) : filteredSubmissions.length > 0 ? (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Title</th>
                    <th>Authors</th>
                    <th>Submission Date</th>
                    <th>Category</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubmissions.map(paper => (
                    <tr key={paper.id}>
                      <td>
                        <Link to={`/paper/${paper.id}`}>{paper.title}</Link>
                      </td>
                      <td>
                        {paper.authors.map(author => author.name).join(', ')}
                      </td>
                      <td>{new Date(paper.submissionDate).toLocaleDateString()}</td>
                      <td>{paper.category}</td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(paper.status)}`}>
                          {paper.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <div className="btn-group">
                          <Link 
                            to={`/paper/${paper.id}`} 
                            className="btn btn-sm btn-outline-primary"
                            title="View Details"
                          >
                            <i className="fas fa-eye"></i>
                          </Link>
                          
                          <button 
                            type="button"
                            className="btn btn-sm btn-outline-secondary dropdown-toggle"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={paper.status === 'WITHDRAWN'}
                          >
                            Actions
                          </button>
                          <ul className="dropdown-menu">
                            {renderStatusOptions(paper)}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-5">
              <p className="mb-0">No submissions found matching the selected filter.</p>
            </div>
          )}
        </div>
      </div>

      {/* Reviewer Assignment Modal */}
      {selectedPaper && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Assign Reviewers</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setSelectedPaper(null)}
                ></button>
              </div>
              <div className="modal-body">
                <h6>Paper: {selectedPaper.title}</h6>
                <p>
                  <strong>Authors:</strong> {selectedPaper.authors.map(author => author.name).join(', ')}
                </p>
                
                <hr />
                
                <h6 className="mb-3">Select Reviewers:</h6>
                <div className="list-group">
                  {availableReviewers.map(reviewer => (
                    <button
                      key={reviewer.id}
                      type="button"
                      className={`list-group-item list-group-item-action ${
                        selectedReviewers.includes(reviewer.id) ? 'active' : ''
                      }`}
                      onClick={() => handleReviewerSelection(reviewer.id)}
                    >
                      <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">{reviewer.name}</h6>
                        {selectedReviewers.includes(reviewer.id) && (
                          <i className="fas fa-check"></i>
                        )}
                      </div>
                      <p className="mb-1">Expertise: {reviewer.expertise}</p>
                    </button>
                  ))}
                </div>
                
                <div className="alert alert-info mt-3">
                  <small>
                    <i className="fas fa-info-circle me-2"></i>
                    Select 2-3 reviewers for an optimal review process.
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setSelectedPaper(null)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => handleAssignReviewers(selectedPaper.id)}
                  disabled={selectedReviewers.length === 0 || actionLoading}
                >
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Assigning...
                    </>
                  ) : 'Assign Reviewers'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Editor Statistics */}
      <div className="row">
        <div className="col-md-4 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h4 className="mb-0">Submissions by Status</h4>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between mb-3">
                <div>Submitted:</div>
                <div>{submissions.filter(p => p.status === 'SUBMITTED').length}</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Under Review:</div>
                <div>{submissions.filter(p => p.status === 'UNDER_REVIEW').length}</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Revisions Required:</div>
                <div>{submissions.filter(p => p.status === 'REVISIONS_REQUIRED').length}</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Accepted:</div>
                <div>{submissions.filter(p => p.status === 'ACCEPTED').length}</div>
              </div>
              <div className="d-flex justify-content-between mb-3">
                <div>Rejected:</div>
                <div>{submissions.filter(p => p.status === 'REJECTED').length}</div>
              </div>
              <div className="d-flex justify-content-between">
                <div>Published:</div>
                <div>{submissions.filter(p => p.status === 'PUBLISHED').length}</div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="col-md-4 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h4 className="mb-0">Submissions by Category</h4>
            </div>
            <div className="card-body">
              {/* Group submissions by category and display count */}
              {Object.entries(
                submissions.reduce((acc, paper) => {
                  acc[paper.category] = (acc[paper.category] || 0) + 1;
                  return acc;
                }, {})
              ).map(([category, count]) => (
                <div key={category} className="d-flex justify-content-between mb-3">
                  <div>{category}:</div>
                  <div>{count}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        <div className="col-md-4 mb-4">
          <div className="card h-100">
            <div className="card-header">
              <h4 className="mb-0">Editor Guidelines</h4>
            </div>
            <div className="card-body">
              <ul className="list-unstyled">
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Initial screening within 7 days
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Assign 2-3 reviewers per submission
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Follow up with reviewers after 3 weeks
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Complete review process within 8 weeks
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Provide detailed feedback to authors
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorDashboard;
